import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { INITIAL_LIBRARY_FILTERS } from '../../lib/library/data';
import type {
  LibraryCandidateKind,
  LibraryDecision,
  LibraryFilters,
  LibrarySort,
  LibraryStage,
  LibrarySource,
  LibraryState,
} from '../../lib/library/types';
import { useLibraryCollections, useLibraryOverview, useLibraryRoles } from '../../hooks/useLibraryApi';

interface LibraryController {
  state: Pick<LibraryState, 'searchQuery' | 'sort' | 'filters'> & {
    roles: LibraryState['roles'];
    collections: LibraryState['collections'];
    candidates: [];
    buckets: LibraryState['buckets'];
  };
  isLoading: boolean;
  setupRequired: boolean;
  error: Error | null;
  setSearchQuery: (value: string) => void;
  setSort: (value: LibrarySort) => void;
  setFilters: (value: LibraryFilters) => void;
  resetFilters: () => void;
}

const LibraryContext = createContext<LibraryController | null>(null);

export function LibraryProvider({ children }: { children: ReactNode }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState<LibrarySort>('newest');
  const [filters, setFilters] = useState<LibraryFilters>(INITIAL_LIBRARY_FILTERS);
  const overviewQuery = useLibraryOverview();
  const rolesQuery = useLibraryRoles();
  const collectionsQuery = useLibraryCollections();

  const value = useMemo<LibraryController>(() => {
    const roles = rolesQuery.data?.length
      ? rolesQuery.data
      : overviewQuery.data?.roles ?? [];
    const collections = collectionsQuery.data ?? [];
    const buckets = {
      offlineCvs: overviewQuery.data?.counts.offlineCvs ?? 0,
    };

    return {
      state: {
        roles,
        collections,
        candidates: [],
        searchQuery,
        sort,
        filters,
        buckets,
      },
      isLoading:
        overviewQuery.isLoading ||
        rolesQuery.isLoading ||
        collectionsQuery.isLoading,
      setupRequired: Boolean(overviewQuery.data?.setupRequired),
      error:
        (overviewQuery.error as Error | null) ||
        (rolesQuery.error as Error | null) ||
        (collectionsQuery.error as Error | null) ||
        null,
      setSearchQuery,
      setSort,
      setFilters,
      resetFilters: () => setFilters(INITIAL_LIBRARY_FILTERS),
    };
  }, [
    collectionsQuery.data,
    collectionsQuery.error,
    collectionsQuery.isLoading,
    filters,
    overviewQuery.data,
    overviewQuery.error,
    overviewQuery.isLoading,
    rolesQuery.data,
    rolesQuery.error,
    rolesQuery.isLoading,
    searchQuery,
    sort,
  ]);

  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
}

export function useLibrary() {
  const context = useContext(LibraryContext);
  if (!context) {
    throw new Error('useLibrary must be used inside LibraryProvider');
  }
  return context;
}

export function useLibraryDatabaseCounts() {
  const overviewQuery = useLibraryOverview();

  return useMemo(
    () => ({
      allCandidates: overviewQuery.data?.counts.allCandidates ?? 0,
      talentPool: overviewQuery.data?.counts.talentPool ?? 0,
      offlineCvs: overviewQuery.data?.counts.offlineCvs ?? 0,
      setupRequired: Boolean(overviewQuery.data?.setupRequired),
      isLoading: overviewQuery.isLoading,
      error: overviewQuery.error as Error | null,
    }),
    [overviewQuery.data?.counts, overviewQuery.data?.setupRequired, overviewQuery.error, overviewQuery.isLoading],
  );
}

export function buildLibraryCandidateQuery(input: {
  roleId?: string;
  collectionId?: string;
  q?: string;
  sort: LibrarySort;
  stages: LibraryStage[];
  sources: LibrarySource[];
  decisions: Exclude<LibraryDecision, 'none'>[];
  locations: string[];
  collectionIds: string[];
  candidateKinds: LibraryCandidateKind[];
  starredOnly: boolean;
}) {
  const collectionIds = input.collectionId
    ? [...new Set([...input.collectionIds, input.collectionId])]
    : input.collectionIds;

  return {
    roleId: input.roleId,
    q: input.q,
    sort: input.sort,
    stages: input.stages,
    sources: input.sources,
    decisions: input.decisions,
    locations: input.locations,
    collectionIds,
    candidateKinds: input.candidateKinds,
    starredOnly: input.starredOnly,
  };
}
